import FileExplorer from '@/components/FileExplorer/FileExplorer';
import {
  fetchComplianceDocuments,
  fetchComplianceDocumentsByFolder,
  fetchComplianceDocumentsFolderStructure,
} from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import { styled } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { ComplianceDocument } from '@/lib/types';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import { useBasketContext } from '@/contexts/BasketContext';
import BasketBar from '@/components/ShareBasketModal/BasketBar';
import { useFixedBasketBarClearance } from '@/lib/bottomOverlays';
import ShareBasketModal from '@/components/ShareBasketModal/ShareBasketModal';
import { FileTypesComplianceGame } from '@/constants/FileTypesCompliance';

const FiltersInitialState = {
  market: [],
  fileTypeCompliance: [],
  fileFormatCompliance: [],
};

const GameComplianceDocuments = ({
  game_id,
  game_name,
  initialFilters,
}: {
  game_id: number;
  game_name?: string;
  initialFilters?: Partial<FilterSchema>;
}) => {
  const { idsFor, addMany, removeMany, count } =
    useBasketContext();
  const barClearance = useFixedBasketBarClearance();
  const [shareOpen, setShareOpen] = useState(false);
  const selected = useMemo(() => idsFor('compliance_documents'), [idsFor]);

  // FileExplorer hands back the full next selection for this
  // category; reconcile it against the basket rather than replacing
  // the basket, which also holds the other category's picks.
  const handleSelect = useCallback(
    (next: number[]) => {
      const nextSet = new Set(next);
      addMany('compliance_documents', next);
      removeMany(
        'compliance_documents',
        idsFor('compliance_documents').filter((id) => !nextSet.has(id)),
      );
    },
    [addMany, removeMany, idsFor],
  );
  const [path, setPath] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const [filters, setFilters] = useState<Partial<FilterSchema>>(
    () => ({ ...FiltersInitialState, ...initialFilters }),
  );

  const {
    folderStructure,
    filesData,
    filesFlatData,
    loading,
    loadingNextPage,
    totalCount,
  } = useFileExplorerData({
    filters,
    path,
    gameIds: [game_id],
    folderStructureFn: fetchComplianceDocumentsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.compliance_documents.folderStructure,
    fetchFilesByFolderFn: fetchComplianceDocumentsByFolder,
    fetchFilesByFolderQueryKey:
      assetQueryKeys.compliance_documents.byFolder,
    fetchFilesFlatFn: fetchComplianceDocuments,
    fetchFilesFlatQueryKey: assetQueryKeys.compliance_documents.flat,
  });

  // The basket spans sections and games; this tab's Download must only act
  // on rows actually on screen, so scope it to what this tab loaded.
  const loadedIds = useMemo(
    () =>
      [
        ...((filesData as { id: number }[] | undefined) ?? []),
        ...((filesFlatData as { id: number }[] | undefined) ?? []),
      ].map((file) => file.id),
    [filesData, filesFlatData],
  );

  const viewSelected = useMemo(() => {
    const onScreen = new Set(
      [
        ...((filesData as ComplianceDocument[] | undefined) ?? []),
        ...((filesFlatData as ComplianceDocument[] | undefined) ?? []),
      ].map((file) => file.id),
    );
    return selected.filter((id) => onScreen.has(id));
  }, [filesData, filesFlatData, selected]);

  const { download, loading: downloadLoading } = useDownload();

  const handleDownloadClick = () => {
    if (!viewSelected.length) return;

    const downloadParams: {
      category: 'compliance_documents';
      file_ids: number[];
      extension?: string;
      entity_name?: string;
      game_name?: string;
    } = {
      category: 'compliance_documents',
      file_ids: viewSelected,
      game_name,
    };

    if (viewSelected.length === 1) {
      const allFiles = [
        ...(filesData as ComplianceDocument[]),
        ...(filesFlatData as ComplianceDocument[]),
      ];
      const selectedFile = allFiles.find(
        (file) => file.id === viewSelected[0],
      );
      if (selectedFile?.extension) {
        downloadParams.extension = selectedFile.extension;
      }
      if (selectedFile?.name) {
        downloadParams.entity_name = selectedFile.name;
      }
    }

    download(downloadParams);
  };

  return (
    <Root $barVisible={count > 0} $barClearance={barClearance}>
      <FileExplorer
        folderStructure={folderStructure}
        files={filesData as ComplianceDocument[]}
        filesFlatList={filesFlatData as ComplianceDocument[]}
        loading={loading}
        selected={selected}
        onSelect={handleSelect}
        selectedCount={viewSelected.length}
        showSelectedLabel={false}
        onDownloadClick={handleDownloadClick}
        showFilters
        filters={filters}
        onFiltersChange={setFilters}
        fileTypeComplianceConfig={FileTypesComplianceGame}
        path={path}
        onPathChange={setPath}
        currentView={currentView}
        onViewChange={setCurrentView}
        downloadLoading={downloadLoading}
        loadingNextPage={loadingNextPage}
      />
      <BasketBar
        variant="fixed"
        category="compliance_documents"
        loadedIds={loadedIds}
        totalCount={totalCount}
        onShareClick={() => setShareOpen(true)}
      />
      <ShareBasketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$barVisible' && prop !== '$barClearance',
})<{ $barVisible: boolean; $barClearance: number }>(
  ({ $barVisible, $barClearance }) => ({
    padding: 24,
    // Reserve room so the fixed bar never covers the last row or the
    // infinite-scroll trigger.
    paddingBottom: $barVisible ? $barClearance : 24,
  }),
);

export default GameComplianceDocuments;
